import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Package, Zap, Wrench, Hammer, Crosshair, FoldHorizontal, Cylinder, Construction, ShieldCheck, Settings, Palette } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import CTASection from "@/components/CTASection";
import { useLanguage } from "@/hooks/useLanguage";

export default function Products() {
  const { t } = useLanguage();

  const categories: { icon: LucideIcon; titleKey: string; descKey: string; image: string; link: string; count: string }[] = [
    { icon: Package, titleKey: "industrialSupply", descKey: "industrialSupplyShort", image: "/images/electrical-supply.jpg", link: "#industrial", count: "1000+ items" },
    { icon: Zap, titleKey: "electricalSupply", descKey: "electricalSupplyShort", image: "/images/electrical-supply.jpg", link: "#electrical", count: "Full range" },
    { icon: Wrench, titleKey: "mechanicalSupply", descKey: "mechanicalSupplyShort", image: "/images/mechanical-supply.jpg", link: "#mechanical", count: "Project packs" },
    { icon: Hammer, titleKey: "civilMaterials", descKey: "civilMaterialsDesc", image: "/images/civil-materials.jpg", link: "#civil", count: "BOQ-based" },
    { icon: Crosshair, titleKey: "cncLaserCutting", descKey: "cncLaserCuttingDesc", image: "/images/cnc-laser-cutting.jpg", link: "/cnc", count: "Any thickness" },
    { icon: FoldHorizontal, titleKey: "pressBrakeBending", descKey: "pressBrakeBendingDesc", image: "/images/press-brake-bending.jpg", link: "/cnc", count: "Custom angles" },
    { icon: Cylinder, titleKey: "tubePipeFab", descKey: "tubePipeFabDesc", image: "/images/tube-fabrication.jpg", link: "/cnc", count: "All sections" },
    { icon: Construction, titleKey: "steelFab", descKey: "steelFabDesc", image: "/images/steel-fabrication.jpg", link: "/steel", count: "Heavy duty" },
    { icon: ShieldCheck, titleKey: "stainlessSteel", descKey: "stainlessSteelDesc", image: "/images/products/stainless-fabrication.jpg", link: "/steel", count: "SS 304/316" },
    { icon: Settings, titleKey: "elecInstSupports", descKey: "elecInstSupportsDesc", image: "/images/products/junction-box-supports.jpg", link: "/steel", count: "Field ready" },
    { icon: Palette, titleKey: "powderCoating", descKey: "powderCoatingCapDesc", image: "/images/powder-coating.jpg", link: "/coating", count: "Project colors" },
    { icon: Package, titleKey: "customProject", descKey: "customProjectDesc", image: "/images/project-support.jpg", link: "/contact", count: "Any quantity" },
  ];

  return (
    <>
      <PageHeader titleKey="productsTitle" subtitleKey="productsSubtitle" />

      <section className="bg-steel-light section-padding px-4 sm:px-6 lg:px-8">
        <div className="content-container">
          <div className="text-center mb-10">
            <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="label-overline text-steel mb-2">{t("browseByCategory")}</motion.p>
            <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }} className="font-display font-bold text-2xl md:text-3xl text-primary-deep">{t("twelveCategories")}</motion.h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {categories.map((cat, i) => {
              const Icon = cat.icon;
              const isExternal = cat.link.startsWith("#");
              const card = (
                <div className="group relative overflow-hidden rounded-xl bg-white border border-muted-border/60 transition-all duration-300 hover:shadow-card-hover hover:-translate-y-1 cursor-pointer">
                  <div className="relative h-40 overflow-hidden">
                    <img src={cat.image} alt={t(cat.titleKey)} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" loading="lazy" />
                    <div className="absolute inset-0 bg-primary-deep/40 group-hover:bg-primary-deep/20 transition-colors duration-300" />
                    <div className="absolute top-3 right-3 px-2.5 py-1 rounded-md bg-white/90 backdrop-blur-sm text-xs font-semibold text-primary-deep">{cat.count}</div>
                    <div className="absolute bottom-3 left-3 w-10 h-10 rounded-lg bg-white/90 backdrop-blur-sm flex items-center justify-center">
                      <Icon className="w-5 h-5 text-primary-deep" />
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-display font-bold text-base text-graphite mb-1 group-hover:text-secondary-blue transition-colors">{t(cat.titleKey)}</h3>
                    <p className="text-sm text-steel">{t(cat.descKey)}</p>
                    <div className="mt-3 flex items-center gap-1 text-sm font-medium text-secondary-blue opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      {t("viewAll")} <ArrowRight className="w-3.5 h-3.5" />
                    </div>
                  </div>
                </div>
              );
              return (
                <motion.div key={cat.titleKey} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.5, delay: i * 0.07 }}>
                  {isExternal ? card : <Link to={cat.link} className="block">{card}</Link>}
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-primary-deep py-12 px-4 sm:px-6 lg:px-8">
        <div className="content-container">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[{ num: "Any", label: "Steel Thickness" }, { num: "Any", label: "Quantity" }, { num: "BOQ", label: "Based Supply" }, { num: "DWG", label: "Based Fabrication" }].map((stat, i) => (
              <motion.div key={stat.label} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}>
                <div className="font-display font-extrabold text-2xl md:text-3xl text-gold mb-1">{stat.num}</div>
                <div className="text-sm text-white/60">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white section-padding px-4 sm:px-6 lg:px-8">
        <div className="content-container">
          <div className="text-center mb-10">
            <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="label-overline text-steel mb-2">{t("quickReference")}</motion.p>
            <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="font-display font-bold text-2xl md:text-3xl text-primary-deep">{t("typicalOutputs")}</motion.h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { title: "CNC Laser Cut", items: ["Mounting plates", "Base plates", "Gussets", "Rails & strips", "Covers & guards"], img: "/images/cnc-laser-cutting.jpg" },
              { title: "Press Brake Formed", items: ["L / U / Z brackets", "Cantilever brackets", "Channels & profiles", "Hoods & enclosures"], img: "/images/press-brake-bending.jpg" },
              { title: "Tube Fabricated", items: ["Instrument stands", "Lighting poles", "CCTV poles", "Handrails & barriers"], img: "/images/tube-fabrication.jpg" },
              { title: "Heavy Fabrication", items: ["Equipment skids", "Structural frames", "Platforms & walkways", "Stairs & ladders"], img: "/images/steel-fabrication.jpg" },
              { title: "Elec. & Inst. Supports", items: ["Junction box brackets", "Panel stands", "Cable cleat plates", "Multi-tier frames"], img: "/images/electrical-supply.jpg" },
              { title: "Coated Components", items: ["Powder coated brackets", "Epoxy coated frames", "Painted covers", "Custom finishes"], img: "/images/powder-coating.jpg" },
            ].map((card, i) => (
              <motion.div key={card.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ delay: i * 0.08 }} className="flex gap-4 bg-steel-light rounded-xl p-4 border border-muted-border/40">
                <img src={card.img} alt={card.title} className="w-24 h-24 rounded-lg object-cover shrink-0" loading="lazy" />
                <div>
                  <h4 className="font-display font-bold text-sm text-graphite mb-2">{card.title}</h4>
                  <ul className="space-y-1">
                    {card.items.map((item) => (
                      <li key={item} className="flex items-center gap-1.5 text-xs text-steel">
                        <span className="w-1 h-1 rounded-full bg-secondary-blue shrink-0" />{item}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <CTASection titleKey="needSpecific" descKey="needSpecificDesc" />
    </>
  );
}
